import axiosInstance from './axiosInstance';

export interface DesignScreenshotAsset {
  full?: string;
  thumb?: string;
  video?: string;
}

export interface DesignScreenshotThumb {
  page_id: string;
  page_name: string;
  screenshot_thumb_url: string;
  video_generation_status?: string;
  responsive_screenshots?: {
    [viewport: string]: DesignScreenshotAsset;
  };
}

interface Waiter {
  resolve: (shots: DesignScreenshotThumb[]) => void;
  reject: (err: unknown) => void;
}

/* Coalescing window: long enough to capture every card mounted in the same
   render commit (plus StrictMode's immediate remount), short enough to be
   invisible next to the request itself. */
const BATCH_WINDOW_MS = 25;
const MAX_BATCH_SIZE = 50;

let pendingWaiters = new Map<string, Waiter[]>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

async function requestBatch(ids: string[], waiters: Map<string, Waiter[]>): Promise<void> {
  try {
    const res = await axiosInstance.get<Record<string, DesignScreenshotThumb[]>>(
      '/api/v1/projects/design-pages/screenshots',
      { params: { project_ids: ids.join(',') } },
    );
    const data = res.data ?? {};

    ids.forEach((id) => waiters.get(id)?.forEach((w) => w.resolve(data[id] ?? [])));
  } catch (err) {
    ids.forEach((id) => waiters.get(id)?.forEach((w) => w.reject(err)));
  }
}

function flush(): void {
  flushTimer = null;

  const waiters = pendingWaiters;

  pendingWaiters = new Map();

  const ids = Array.from(waiters.keys());

  for (let i = 0; i < ids.length; i += MAX_BATCH_SIZE) {
    void requestBatch(ids.slice(i, i + MAX_BATCH_SIZE), waiters);
  }
}

/**
 * Fetch design screenshot thumbs for one project. Calls made within the same
 * batching window share a single batch API request, so a grid of N project
 * cards produces 1 HTTP call instead of N.
 */
export function fetchProjectScreenshots(projectId: string): Promise<DesignScreenshotThumb[]> {
  return new Promise((resolve, reject) => {
    const list = pendingWaiters.get(projectId) ?? [];

    list.push({ resolve, reject });
    pendingWaiters.set(projectId, list);

    if (!flushTimer) flushTimer = setTimeout(flush, BATCH_WINDOW_MS);
  });
}
